import { Controller } from 'stimulus'

// Plays Help Center videos (HelpHelper#help_video_button) in a dialog over the
// current screen. The controller sits on the layout's page wrapper beside
// help-drawer, so Watch buttons in the "Learn more" drawer work too. The
// dialog markup is layouts/application/_help_drawer; its contents are
// help/_video_player, fetched fresh each time so the signed video URL is new.
export default class extends Controller {
  static targets = ['scrim', 'dialog']

  open(event) {
    event.preventDefault()
    const button = event.currentTarget
    this.opener = button

    const url = new URL(button.dataset.helpVideoUrl, window.location.origin)
    if (button.dataset.helpVideoAt) url.searchParams.set('t', button.dataset.helpVideoAt)
    if (button.dataset.helpVideoQuery) url.searchParams.set('q', button.dataset.helpVideoQuery)

    this.scrimTarget.hidden = false
    this.dialogTarget.innerHTML = '<div class="help-drawer-loading">Loading the video&hellip;</div>'

    // A slow answer for a video already closed must not fill the dialog later.
    if (this.request) this.request.abort()
    this.request = new AbortController()

    fetch(url, { credentials: 'same-origin', signal: this.request.signal, headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(response => {
        if (!response.ok || response.redirected) throw new Error(`Video request failed: ${response.status}`)
        return response.text()
      })
      .then(html => {
        this.dialogTarget.innerHTML = html
        this.dialogTarget.focus()
        this.wire()
      })
      .catch(error => {
        if (error.name === 'AbortError') return
        this.dialogTarget.innerHTML = '<div class="help-drawer-loading">This video could not be loaded. Try again in a moment.</div>'
      })
  }

  wire() {
    const video = this.dialogTarget.querySelector('[data-help-video-media]')
    const player = this.dialogTarget.querySelector('[data-help-video-start]')
    const start = parseFloat(player ? player.dataset.helpVideoStart : '0') || 0
    this.video = video

    if (video) {
      // Closing pauses the video, which fires one more timeupdate after
      // this.video is cleared, so the listeners keep their own element.
      video.addEventListener('timeupdate', () => this.mark(video.currentTime, false))
      if (start > 0) {
        video.addEventListener('loadedmetadata', () => { video.currentTime = start }, { once: true })
      }
    }
    this.mark(start, start > 0)
  }

  seek(event) {
    const time = parseFloat(event.currentTarget.dataset.seek) || 0
    if (this.video) {
      this.video.currentTime = time
      this.video.play().catch(() => {})
    }
    this.mark(time, false)
  }

  // Highlights the transcript line being spoken.
  mark(time, scroll) {
    let current = null
    this.dialogTarget.querySelectorAll('[data-cue]').forEach(line => {
      // A search hit starts on the whole second before its line, so allow a little lead.
      if (parseFloat(line.dataset.cue) <= time + 0.9) current = line
    })
    this.dialogTarget.querySelectorAll('[data-cue]').forEach(line => line.classList.toggle('is-current', line === current))
    if (current && scroll) current.scrollIntoView({ block: 'nearest' })
  }

  close() {
    if (this.scrimTarget.hidden) return
    if (this.request) this.request.abort()
    if (this.video) this.video.pause()
    this.video = null
    this.dialogTarget.innerHTML = ''
    this.scrimTarget.hidden = true
    if (this.opener) this.opener.focus()
  }

  closeOnScrim(event) {
    if (event.target === this.scrimTarget) this.close()
  }

  keydown(event) {
    if (event.key === 'Escape') this.close()
  }
}
